import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';

interface TransitionOption {
  status: number | string;
  label: string;
}

interface MyTask {
  id: string;
  projectId?: string;
  title: string;
  description?: string;
  status: number;
  statusLabel?: string;
  priority?: number;
  assigneeId?: string;
  teamLeadId?: string;
  dueDate?: string;
  isOverdue?: boolean;
  delayLabel?: string;
  remainingLabel?: string;
  estimatedHours?: number;
  actualHours?: number;
  allowedTransitions?: TransitionOption[];
}

@Component({
  selector: 'app-my-work',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule
  ],
  template: `
<div class="my-work">
  <header>
    <div>
      <h1>My Work</h1>
      <p>
        User mode — {{ displayName() }}. Only tasks assigned to you are listed.
        Start work with <strong>In progress</strong>, or move to the next status when ready.
      </p>
    </div>
    <button mat-stroked-button type="button" (click)="loadTasks()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
  </header>

  @if (error()) {
    <div class="error">{{ error() }}</div>
  }

  @if (loading()) {
    <mat-spinner diameter="36"></mat-spinner>
  }

  <div class="task-grid">
    @for (task of tasks(); track task.id) {
      <mat-card class="task-card" [class.overdue]="task.isOverdue">
        <div class="task-head">
          <h3>{{ task.title }}</h3>
          <span class="status" [class.overdue]="task.isOverdue">{{ task.statusLabel || statusFallback(task.status) }}</span>
        </div>
        <p>{{ task.description || 'No description' }}</p>
        <div class="meta">
          @if (task.dueDate) {
            <span class="due" [class.overdue]="task.isOverdue">
              <mat-icon>event</mat-icon>
              {{ task.dueDate }}{{ task.isOverdue ? ' · Overdue' : '' }}
            </span>
          }
          @if (task.remainingLabel) {
            <span>{{ task.remainingLabel }}</span>
          }
          @if (task.estimatedHours) {
            <span>ETA {{ task.estimatedHours }}h</span>
          }
          @if (task.actualHours) {
            <span>Tracked {{ task.actualHours }}h</span>
          }
        </div>
        <div class="actions">
          @for (t of task.allowedTransitions || []; track t.status) {
            <button mat-stroked-button type="button" (click)="updateStatus(task, t.status)">
              {{ t.label }}
            </button>
          }
          @empty {
            <span class="empty">No status changes available</span>
          }
        </div>
      </mat-card>
    } @empty {
      @if (!loading()) {
        <mat-card>
          <p>No tasks are assigned to you yet. Ask your team lead to allocate work.</p>
        </mat-card>
      }
    }
  </div>
</div>
  `,
  styles: [
    `
      .my-work { max-width: 1100px; }
      header {
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        align-items: flex-start;
        margin-bottom: 1.25rem;
      }
      h1 {
        margin: 0 0 0.4rem;
        font-size: clamp(1.55rem, 1.3rem + 0.8vw, 1.9rem);
        font-weight: 700;
        letter-spacing: -0.03em;
      }
      header p {
        margin: 0;
        color: var(--tt-muted);
        max-width: 42rem;
        line-height: 1.5;
      }
      .error { margin-bottom: 1rem; }
      .task-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 0.9rem;
      }
      .task-card { padding: 1.05rem 1.1rem; }
      .task-card.overdue { border-left: 3px solid var(--tt-danger, #dc2626); }
      .task-head {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
        align-items: flex-start;
      }
      .task-head h3 {
        margin: 0;
        font-size: 1.05rem;
        letter-spacing: -0.02em;
      }
      .status {
        background: var(--tt-primary-soft);
        color: var(--tt-primary-dark);
        border-radius: 999px;
        padding: 0.2rem 0.65rem;
        font-size: 0.72rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .status.overdue { background: #fef2f2; color: #b91c1c; }
      .meta .due {
        display: inline-flex;
        align-items: center;
        gap: 0.2rem;
      }
      .meta .due.overdue { color: #b91c1c; font-weight: 600; }
      .meta .due mat-icon { font-size: 16px; width: 16px; height: 16px; }
      .meta {
        display: flex;
        gap: 0.75rem;
        color: var(--tt-muted);
        font-size: 0.85rem;
        margin-bottom: 0.75rem;
      }
      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
      }
      .empty {
        color: var(--tt-muted);
        font-size: 0.85rem;
      }
    `
  ]
})
export class MyWorkComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly tasks = signal<MyTask[]>([]);
  readonly displayName = this.auth.displayName;

  ngOnInit(): void {
    this.loadTasks();
  }

  loadTasks(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getPaged<MyTask>('v1/tasks', { page: 1, pageSize: 100, mine: true } as never).subscribe({
      next: (page) => {
        this.tasks.set(page.items ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load your tasks.');
      }
    });
  }

  updateStatus(task: MyTask, toStatus: number | string): void {
    this.api
      .post(`v1/tasks/${task.id}/transition`, {
        toStatus: typeof toStatus === 'string' ? Number(toStatus) : toStatus,
        action: 'user_mode',
        comment: null
      })
      .subscribe({
        next: () => this.loadTasks(),
        error: (err) => this.error.set(err?.message || 'Failed to update status.')
      });
  }

  statusFallback(status: number): string {
    const labels = [
      'Pending distribution',
      'In progress',
      'Paused',
      'Send to Developer',
      'Send to QA',
      'Send to ASO',
      'Completed',
      'Published',
      'Reopen',
      'Update required',
      'Draft',
      'Assigned to Team Lead',
      'Accepted by Team Lead',
      'Distributed',
      'Submitted for Review',
      'Team Lead Review',
      'Rework Required'
    ];
    return labels[status] ?? String(status);
  }
}
